import React, { FC, useEffect, useMemo, useState } from 'react'
import Modal from '../common/Modal'
import AddColorModule from './AddColorModule'
import AddLabelGroupCategoryModule from './AddLabelGroupCategoryModule'
import AddMemberModule from './AddMemberModule'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AddLabelGroupModule from './AddLabelGroupModule'
import COLOR_LIST from '../../constants/taskColor'
import { useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import ViewLabelGroupModal from './ViewLabelGroupModal'

interface MemberDetail {
  member_id: number
  name: string
  kana: string | null
  profile_image: string | null
}

interface LabelGroup {
  id: number
  name: string
  code: string | null
  color: string
}

interface CategoryList {
  id: number
  category_name: string
  color: string
  parent_id: number | null
  members: MemberDetail[]
  parent_members: MemberDetail[]
}

interface AddParam {
  category_name: string
  color: string
  parent_id?: number | null
  member_ids?: number[]
  label_group_ids?: number[]
}

interface ModParam {
  id: number
  category_name?: string
  color?: string
  parent_id?: number | null
  member_ids?: number[]
  add_label_group_ids?: number[]
  remove_label_group_ids?: number[]
}

interface ModalState {
  id?: number
  name?: string
  color?: string
  parent_id?: number | null
  member_ids?: number[]
  label_group_ids?: number[]
  type: string
}

interface Props {
  modalState: ModalState
  closeCallback: VoidFunction
  addCallback: (param: AddParam) => void
  modCallback?: (param: ModParam) => void
  isDisplay: boolean
  categoryList: CategoryList[]
  memberList: MemberDetail[]
  labelGroupList: LabelGroup[]
}

const AddLabelGroupCategoryModal: FC<Props> = (props) => {
  const {
    modalState,
    closeCallback,
    addCallback,
    modCallback,
    isDisplay,
    categoryList,
    memberList,
    labelGroupList,
  } = props

  const {
    login,
  } = useSelector((state: RootState) => state)

  const [labelGroupCategoryName, setLabelGroupCategoryName] = useState<string>("")
  const [labelGroupCategoryColor, setLabelGroupCategoryColor] = useState<string>("#f2a2a2")
  const [labelGroupCategoryParentId, setLabelGroupCategoryParentId] = useState<number | null>(null)

  const [selectMembers, setSelectMembers] = useState<MemberDetail[]>([])
  const [selectLabelGroups, setSelectLabelGroups] = useState<LabelGroup[]>([])

  const [isOpenOption, setIsOpenOption] = useState<boolean>(false)
  const [isOpenCategory, setIsOpenCategory] = useState<boolean>(false)

  const [isDisplayViewLabelGroupModal, setIsDisplayViewLabelGroupModal] = useState<boolean>(false)
  const [selectAddLabelGroups, setSelectAddLabelGroups] = useState<LabelGroup[]>([])
  const [selectRemoveLabelGroups, setSelectRemoveLabelGroups] = useState<LabelGroup[]>([])

  const parentMembers = useMemo(() => {
    const target = categoryList.find((v) => v.id === labelGroupCategoryParentId)
    if (!target) return []
    return Array.from(new Map([...target.parent_members, ...target.members].map((v) => [v.member_id, v])).values())
  }, [labelGroupCategoryParentId])

  const randomInt = (min: number, max: number) => {
    const intMin = Math.ceil(min)
    const intMax = Math.floor(max)
    return Math.floor(Math.random() * (intMax - intMin) + intMin)
  }

  const randomColor = () => COLOR_LIST.map((v) => v.values).flat()[randomInt(0, 23)].color

  const resetParam = () => {
    setLabelGroupCategoryName("")
    setLabelGroupCategoryColor("#f2a2a2")
    setLabelGroupCategoryParentId(null)
    setSelectMembers([])
    setSelectLabelGroups([])
    setIsOpenOption(false)
    setSelectAddLabelGroups([])
    setSelectRemoveLabelGroups([])
  }

  const closeModal = () => {
    closeCallback()
    resetParam()
  }

  const submitLabelGroupCategory = () => {
    if (modalState.type === "create") {
      const tmpValue: AddParam = {
        category_name: labelGroupCategoryName,
        color: labelGroupCategoryColor,
      }
      if (labelGroupCategoryParentId) tmpValue.parent_id = labelGroupCategoryParentId
      if (selectMembers.length) tmpValue.member_ids = selectMembers.map((v) => v.member_id)
      if (selectLabelGroups.length) tmpValue.label_group_ids = selectLabelGroups.map((v) => v.id)
      if (selectAddLabelGroups.length) tmpValue.label_group_ids = selectAddLabelGroups.map((v) => v.id)
      addCallback(tmpValue)
    } else {
      if (!modCallback) return
      const tmpValue: ModParam = {
        id: modalState.id || 0,
        category_name: labelGroupCategoryName,
        color: labelGroupCategoryColor,
      }
      if (labelGroupCategoryParentId) tmpValue.parent_id = labelGroupCategoryParentId
      if (selectMembers.length) tmpValue.member_ids = selectMembers.map((v) => v.member_id)
      if (selectLabelGroups.length) {
        const selectIds = selectLabelGroups.map((v) => v.id)
        const currentIds = modalState.label_group_ids || []
        tmpValue.add_label_group_ids = selectIds.filter((v) => !currentIds.includes(v))
        tmpValue.remove_label_group_ids = currentIds.filter((v) => !selectIds.includes(v))
      }
      if (selectAddLabelGroups.length) tmpValue.add_label_group_ids = selectAddLabelGroups.map((v) => v.id)
      if (selectRemoveLabelGroups.length) tmpValue.remove_label_group_ids = selectRemoveLabelGroups.map((v) => v.id)
      modCallback(tmpValue)
    }
    resetParam()
  }

  const setMember = (members: MemberDetail[]) => {
    setSelectMembers(members)
  }

  const delMember = (memberId: number) => {
    setSelectMembers([...selectMembers.filter((v) => v.member_id !== memberId)])
  }

  const setLabelGroup = (labelGroups: LabelGroup[]) => {
    setSelectLabelGroups(labelGroups)
  }

  const delLabelGroup = (labelGroupId: number) => {
    setSelectLabelGroups([...selectLabelGroups.filter((v) => v.id !== labelGroupId)])
  }

  const toggleCategoryParentIdArea = (isOpen: boolean) => {
    setIsOpenOption(isOpen)
  }

  useEffect(() => {
    setLabelGroupCategoryName(modalState.name || "")
    setLabelGroupCategoryColor(modalState.color || randomColor())
    setLabelGroupCategoryParentId(modalState.parent_id || null)
    setSelectMembers(memberList.filter((v) => modalState.member_ids?.includes(v.member_id)))
    setSelectLabelGroups(labelGroupList.filter((v) => modalState.label_group_ids?.includes(v.id)))
  }, [isDisplay])

  return (
    <>
      <Modal
        title={modalState.type === "create" ? "カテゴリー新規作成" : "カテゴリー編集"}
        show={isDisplay}
        wrapperClass="approver_setting_modal"
        closeCallback={closeModal}
        closeable
      >
        <>
          <div className="item">
            <div className="title">
              カテゴリー名
            </div>
            <div className="detail equal_space row_1">
              <input
                type="text"
                value={labelGroupCategoryName}
                onChange={(e) => setLabelGroupCategoryName(e.target.value)}
              />
            </div>
          </div>
          <div className="item">
            <div className="title">
              カラー
            </div>
            <div className="detail equal_space row_1">
              <AddColorModule
                color={labelGroupCategoryColor}
                changeCallback={(color) => setLabelGroupCategoryColor(color)}
              />
            </div>
          </div>
          <div className="item">
            <div className="title">
              カテゴリー表示ユーザー
            </div>
            <fieldset className="member_area_wrapper">
              <div className="member_area">
                {
                  !!labelGroupCategoryParentId && (
                    <div
                      style={{
                        border: "solid 1px #4372ba",
                        padding: "8px",
                        backgroundColor: "#e2edff",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        親階層のカテゴリー表示ユーザー
                        <button
                          type="button"
                          onClick={() => setIsOpenCategory(!isOpenCategory)}
                          style={{
                            padding: "2px 4px",
                            border: "solid 1px #666666",
                            borderRadius: "4px",
                            fontSize: "9px",
                            backgroundColor: "#FFFFFF",
                          }}
                        >
                          {isOpenCategory ? "閉じる" : "開く"}
                        </button>
                      </div>
                      {
                        isOpenCategory && (
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "4px",
                              marginTop: "8px",
                            }}
                          >
                            {
                              parentMembers.map((member) => (
                                <div
                                  className="member_block"
                                  key={`category_member_${member.member_id}`}
                                >
                                  {
                                    member.profile_image ? (
                                      <img
                                        alt="profile"
                                        src={member.profile_image}
                                        className="profile_icon"
                                      />
                                    ) : (
                                      <img
                                        alt=""
                                        src={`/static/images/profile/${(Number(member.member_id) || 0) % 12}.jpeg`}
                                        className="profile_icon"
                                      />
                                    )
                                  }
                                  {member.name}
                                </div>
                              ))
                            }
                          </div>
                        )
                      }
                    </div>
                  )
                }
                {
                  selectMembers.map((value) => (
                    <div
                      className="member_block"
                      key={`category_member_${value.member_id}`}
                    >
                      {
                        value.profile_image ? (
                          <img
                            alt="profile"
                            src={value.profile_image}
                            className="profile_icon"
                          />
                        ) : (
                          <img
                            alt=""
                            src={`/static/images/profile/${(Number(value.member_id) || 0) % 12}.jpeg`}
                            className="profile_icon"
                          />
                        )
                      }
                      {value.name}
                      <span
                        style={{
                          cursor: "pointer",
                        }}
                        onClick={() => delMember(value.member_id)}
                        role="none"
                      >
                        <FontAwesomeIcon icon={['fas', 'times']} />
                      </span>
                    </div>
                  ))
                }
                <AddMemberModule
                  memberIdList={selectMembers.map((v) => v.member_id)}
                  callbackMembers={(val) => setMember([...memberList.filter((v) => val.map((w) => w.id).includes(v.member_id))])}
                  memberList={memberList.map((v) => ({ id: v.member_id, name: v.name, profile_image: v.profile_image }))}
                />
              </div>
            </fieldset>
          </div>
          {
            !!labelGroupList.length && (
              <div className="item">
                <div className="title" style={{ justifyContent: "space-between" }}>
                  {login.feature_config?.high_volume ? "プロジェクト" : "所属プロジェクト"}
                  {
                    login.feature_config?.high_volume && modalState.type === "update" && (
                      <button
                        type="button"
                        style={{
                          border: "solid 1px #999999",
                          borderRadius: "4px",
                          backgroundColor: "#EEEEEE",
                          color: "#666666",
                          fontSize: "10px",
                          padding: "4px 8px",
                        }}
                        onClick={() => setIsDisplayViewLabelGroupModal(true)}
                      >
                        所属プロジェクトを確認する
                      </button>
                    )
                  }
                </div>
                <fieldset className="member_area_wrapper">
                  {
                    login.feature_config?.high_volume ? (
                      <>
                        <div>
                          追加するプロジェクト
                        </div>
                        <div
                          className="member_area"
                          style={{
                            border: "solid 1px #CCCCCC",
                            borderRadius: "4px",
                            padding: "8px",
                          }}
                        >
                          {
                            selectAddLabelGroups.map((value) => (
                              <div
                                className="member_block"
                                key={`add_category_label_group_${value.id}`}
                                style={{
                                  padding: "8px",
                                }}
                              >
                                <div
                                  style={{
                                    backgroundColor: value.color,
                                    width: "12px",
                                    height: "12px",
                                    borderRadius: "50%",
                                  }}
                                />
                                {value.name}
                                {value.code && `(${value.code})`}
                                <span
                                  style={{
                                    cursor: "pointer",
                                  }}
                                  onClick={() => setSelectAddLabelGroups((cur) => cur.filter((v) => v.id !== value.id))}
                                  role="none"
                                >
                                  <FontAwesomeIcon icon={['fas', 'times']} />
                                </span>
                              </div>
                            ))
                          }
                          <AddLabelGroupModule
                            labelGroupCategoryId={modalState.id || 0}
                            labelGroupIdList={[]}
                            callbackLabelGroups={(val) => setSelectAddLabelGroups((cur) => [
                              ...cur,
                              ...val.filter((v) => !cur.map((w) => w.id).includes(v.id)),
                            ])}
                            labelGroupList={[]}
                            searchType="not_assigned"
                          />
                        </div>
                        {
                          modalState.type === "update" && (
                            <>
                              <div>
                                削除するプロジェクト
                              </div>
                              <div
                                className="member_area"
                                style={{
                                  border: "solid 1px #CCCCCC",
                                  borderRadius: "4px",
                                  padding: "8px",
                                }}
                              >
                                {
                                  selectRemoveLabelGroups.map((value) => (
                                    <div
                                      className="member_block"
                                      key={`remove_category_label_group_${value.id}`}
                                      style={{
                                        padding: "8px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          backgroundColor: value.color,
                                          width: "12px",
                                          height: "12px",
                                          borderRadius: "50%",
                                        }}
                                      />
                                      {value.name}
                                      {value.code && `(${value.code})`}
                                      <span
                                        style={{
                                          cursor: "pointer",
                                        }}
                                        onClick={() => setSelectRemoveLabelGroups((cur) => cur.filter((v) => v.id !== value.id))}
                                        role="none"
                                      >
                                        <FontAwesomeIcon icon={['fas', 'times']} />
                                      </span>
                                    </div>
                                  ))
                                }
                                <AddLabelGroupModule
                                  labelGroupCategoryId={modalState.id || 0}
                                  labelGroupIdList={[]}
                                  callbackLabelGroups={(val) => setSelectRemoveLabelGroups((cur) => [
                                    ...cur,
                                    ...val.filter((v) => !cur.map((w) => w.id).includes(v.id)),
                                  ])}
                                  labelGroupList={[]}
                                  searchType="assigned"
                                />
                              </div>
                            </>
                          )
                        }
                      </>
                    ) : (
                      <>
                        <div className="member_area">
                          {
                            selectLabelGroups.map((value) => (
                              <div
                                className="member_block"
                                key={`category_label_group_${value.id}`}
                                style={{
                                  padding: "8px",
                                }}
                              >
                                <div
                                  style={{
                                    backgroundColor: value.color,
                                    width: "12px",
                                    height: "12px",
                                    borderRadius: "50%",
                                  }}
                                />
                                {value.name}
                                {value.code && `(${value.code})`}
                                <span
                                  style={{
                                    cursor: "pointer",
                                  }}
                                  onClick={() => delLabelGroup(value.id)}
                                  role="none"
                                >
                                  <FontAwesomeIcon icon={['fas', 'times']} />
                                </span>
                              </div>
                            ))
                          }
                          <AddLabelGroupModule
                            labelGroupCategoryId={modalState.id || 0}
                            labelGroupIdList={selectLabelGroups.map((v) => v.id)}
                            callbackLabelGroups={(val) => setLabelGroup(val)}
                            labelGroupList={labelGroupList}
                          />
                        </div>
                      </>
                    )
                  }
                </fieldset>
              </div>
            )
          }
          {
            !isOpenOption ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  margin: "24px 0 8px",
                }}
              >
                <button
                  type="button"
                  className="button"
                  style={{
                    borderRadius: "4px",
                    width: "50%",
                    textAlign: "center",
                    backgroundColor: "#4472ba",
                    border: "1px solid #4472ba",
                    color: "#ffffff",
                  }}
                  onClick={() => toggleCategoryParentIdArea(!isOpenOption)}
                >
                  階層を設定する
                </button>
              </div>
            ) : (
              <div className="item">
                <div className="title" style={{ minHeight: "auto" }}>
                  階層化設定
                </div>
                <div
                  style={{
                    color: "#666666",
                    fontSize: "11px",
                    marginBottom: "8px",
                  }}
                >
                  指定したカテゴリーを親階層に設定することができます
                </div>
                <div className="detail equal_space row_1">
                  <AddLabelGroupCategoryModule
                    labelGroupCategoryId={modalState.id || null}
                    parentId={labelGroupCategoryParentId}
                    changeCallback={(parent_id) => setLabelGroupCategoryParentId(parent_id)}
                    categoryList={categoryList}
                  />
                </div>
              </div>
            )
          }
        </>
        <>
          <button
            type="button"
            className="button cancel"
            onClick={closeModal}
          >
            キャンセル
          </button>
          <button
            type="button"
            className="button"
            onClick={submitLabelGroupCategory}
            disabled={!labelGroupCategoryName}
          >
            {modalState.type === "create" ? "作成する" : "編集する"}
          </button>
        </>
      </Modal>
      <ViewLabelGroupModal
        isDisplay={isDisplayViewLabelGroupModal}
        labelGroupCategory={{ id: modalState.id || 0, category_name: modalState.name || "", color: modalState.color || "#CCCCCC" }}
        closeCallback={() => setIsDisplayViewLabelGroupModal(false)}
      />
    </>
  )
}

AddLabelGroupCategoryModal.defaultProps = {
  modCallback: undefined,
}

export default AddLabelGroupCategoryModal
